import _ from 'lodash';
import {
  computed,
  reactive, ref, Ref, watch,
} from '@vue/composition-api';
import { useQuery, useResult } from '@vue/apollo-composable';
import { useRouter } from '@ideavivat/vivat-2';

import getAreaCodes from '@/gql/areaCode/getAreaCodes.gql';
import getDeliveries from '@/gql/delivery/getDeliveries.gql';
import countDeliveryGroups from '@/gql/deliveryNote/countDeliveryGroups.gql';
import getMostStableTotalItem from '@/gql/deliveryNote/getMostStableTotalItem.gql';
import countResendDelivery from '@/gql/delivery/countResendDelivery.gql';

import { DEFAULT_PAGINATOR_INFO } from '@/types/paginatorInfo';
import useDelivery from '@/useDelivery';
import useAuth from '@/useAuth';

const { sumPackagesPrice } = useDelivery();

export default function useCreateSingleDeliveryNote() {
  const { me } = useAuth();
  const getDeliveryFirstTime = ref(true);
  const { route } = useRouter();

  const filter = reactive({
    deliveryLocationDate: '',
    deliveryAreaSearch: '',
    deliveryNoteSearch: '',
    deliverySearch: '',
    deliveryStatus: '',
    deliverySortInput: 'updatedAt',
    deliverySort: [{ column: 'updatedAt', order: 'DESC' }],
    reservedVehicleId: undefined,
    pendingDeliverySearch: false,
    areas: [],
    sortByArea: [],
    selectedAreasName: [],
    selectedAreasCode: [],
    selectedAreasId: [],
    selectedDestinations: { ...me.hub as any },
    selectedDestinationsId: me.hubId,
    filterByType: 'NORMAL',
    provinceIdSelector: undefined,
  });
  const selectedDeliveriesId: Ref<number[]> = ref([]);
  const selectedDeliveries: Ref<any[]> = ref([]);
  const alreadySavedDeliveries: Ref<any[]> = ref([]);
  watch(() => me.hubId, (newVal: number, oldVal: number) => {
    if (newVal !== oldVal) {
      filter.selectedDestinations = { ...me.hub as any };
      filter.selectedDestinationsId = me.hubId;
    }
  });

  const areaCodesPage = ref(1);
  const { result: getAreaCodesResult, loading: getAreaCodesLoading } = useQuery(getAreaCodes, computed(() => ({
    first: 100,
    filter: filter.deliveryAreaSearch !== '' ? {
      column: 'CODE',
      operator: 'LIKE',
      value: `%${filter.deliveryAreaSearch}%`,
    } : undefined,
    filterHub: (filter.selectedDestinations as any).id ? {
      column: 'ID',
      operator: 'EQ',
      value: (filter.selectedDestinations as any).id,
    } : undefined,
    page: areaCodesPage.value,
  })));
  const areaCodes = computed(() => getAreaCodesResult.value?.areasPaginate.data || []);
  const areaCodesPaginatorInfo = computed(() => getAreaCodesResult.value?.areasPaginate.paginatorInfo || { ...DEFAULT_PAGINATOR_INFO });
  watch(() => getAreaCodesResult.value, (newVal: any, oldVal: any) => {
    if (newVal && newVal !== oldVal) {
      areaCodesPage.value = areaCodesPaginatorInfo.value.currentPage;
    }
  });
  watch(() => filter.deliveryAreaSearch, (newVal: string, oldVal: string) => {
    if (newVal !== oldVal && newVal) areaCodesPage.value = 1;
  });

  function selectedAreaCode(params: any) {
    filter.pendingDeliverySearch = true;
    if (params === -1) {
      filter.selectedAreasId = [];
      filter.selectedAreasCode = [];
      return;
    }
    const idx = filter.selectedAreasId.indexOf(params.id as never);
    if (idx > -1) {
      filter.selectedAreasId.splice(idx, 1);
      filter.selectedAreasCode.splice(idx, 1);
    } else {
      filter.selectedAreasId.push(params.id as never);
      filter.selectedAreasCode.push(params.code as never);
    }
  }
  const computedFilterAreaCodes = computed(() => filter.areas.map((area: any) => area.code));

  /**
   *
   * === Delivery ===
   *
   */
  const { result: getMostStableTotalItemResult } = useQuery(getMostStableTotalItem, computed(() => ({
    filterHubId: me.hub?.id,
    filterByType: filter.filterByType,
    // filterDestinationHubId: filter.selectedDestinationsId || undefined,
    filterByStatus: 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER',
  })), () => ({ fetchPolicy: 'network-only' }));
  const mostStableTotalItem = useResult(getMostStableTotalItemResult, {
    deliveries: { paginatorInfo: { total: 0 } },
    vehicles: { paginatorInfo: { total: 0 } },
  }, (data) => data);

  const { result: getCountResendResult } = useQuery(countResendDelivery, (() => ({
    filterHubId: me.hubId,
    filterByType: filter.filterByType,
    // filterDestinationHubId: filter.selectedDestinationsId || undefined,
  })));
  const countResend = computed(() => getCountResendResult.value?.totalResendDelivery?.paginatorInfo?.total || 0);

  const deliveriesPage = ref(1);
  const deliveriesPerPage = ref(30);
  const deliveries: Ref<any[]> = ref([]);
  const deliverySortHandler = _.debounce(() => {
    filter.pendingDeliverySearch = true;
    filter.deliverySort = [{ column: filter.deliverySortInput, order: 'DESC' }];
  });
  const deliveryNameSearchHandler = _.debounce(() => {
    filter.pendingDeliverySearch = true;
  }, 500);
  const deliveryFilterHandler = _.debounce(() => {
    filter.pendingDeliverySearch = true;
  }, 100);
  const { result: getDeliveriesResult, loading: getDeliveriesLoading } = useQuery(getDeliveries, computed(() => {
    let page = deliveriesPage.value;
    if (filter.pendingDeliverySearch) page = 1;

    const OR = [];
    const AND = [];

    const filteredStatus = ['NOT_SUCCESS', 'SOME_SUCCESS', 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER'];

    if (filter.deliverySearch !== '') {
      OR.push({
        OR: [
          { column: 'CONTACT_NAME', operator: 'LIKE', value: `%${filter.deliverySearch}%` },
          { column: 'ORDER_NUMBER', operator: 'LIKE', value: `%${filter.deliverySearch}%` },
          { column: 'CUSTOMER_NAME', operator: 'LIKE', value: `%${filter.deliverySearch}%` },
        ],
      });
    }

    if (filter.deliveryStatus) {
      AND.push({
        OR: [
          { column: 'STATUS', operator: 'LIKE', value: filter.deliveryStatus },
        ],
      });
    } else if (route.value.params.id && route.value.params.id > -1) {
      AND.push({
        OR: [
          { column: 'DELIVERY_NOTE_ID', operator: 'EQ', value: route.value.params.id },
          { column: 'STATUS', operator: 'IN', value: filteredStatus },
        ],
      });
    } else {
      AND.push({
        column: 'STATUS',
        operator: 'IN',
        value: filteredStatus,
      });
    }

    return {
      page,
      first: deliveriesPerPage.value,
      filter: {
        OR,
        AND,
      },
      filterArea: filter.areas.length !== 0 ? {
        OR: [{ column: 'CODE', operator: 'IN', value: computedFilterAreaCodes.value }],
      } : undefined,
      orderBy: filter.deliverySortInput !== '' ? [{ column: 'createdAt', order: 'ASC' }, { column: 'delivery_note_id', order: 'DESC' }, { column: 'order', order: 'ASC' }, ...filter.deliverySort, ...filter.sortByArea] : undefined,
      filterHubId: me.hubId,
      filterByType: filter.filterByType,
      filterByProvinceId: filter.provinceIdSelector ? filter.provinceIdSelector : undefined,
    };
  }), () => ({ fetchPolicy: 'network-only' }));
  const deliveriesResultSet = useResult(getDeliveriesResult, { data: [], paginatorInfo: {} }, (data) => data.deliveries);
  const deliveriesResult = useResult(getDeliveriesResult, [], (data) => data.deliveries.data);
  const deliveriesPaginatorInfo = ref({ ...DEFAULT_PAGINATOR_INFO });
  watch(() => deliveriesResultSet.value, (newVal, oldVal) => {
    if (!getDeliveriesLoading.value && newVal !== oldVal) {
      const mappedDelivery = deliveriesResult.value.map((delivery: any) => ({
        ...delivery,
        packagesPrice: sumPackagesPrice(delivery.packages),
      }));

      deliveries.value = mappedDelivery;
      filter.pendingDeliverySearch = false;
      deliveriesPaginatorInfo.value = deliveriesResultSet.value.paginatorInfo;
    }
  });

  function selectDelivery(d: any) {
    const idx = selectedDeliveriesId.value.indexOf(d.id as never);
    if (idx > -1) {
      selectedDeliveries.value.splice(idx, 1);
      selectedDeliveriesId.value.splice(idx, 1);
    } else {
      selectedDeliveriesId.value.push(d.id as never);
      selectedDeliveries.value.push(d as never);
    }
  }
  function pendingSelectAllDeliveries() {
    if (selectedDeliveriesId.value.length === deliveries.value.length) {
      selectedDeliveriesId.value = [];
      selectedDeliveries.value = [];
    } else {
      selectedDeliveriesId.value = deliveries.value.map((d: any) => d.id);
      selectedDeliveries.value = [...deliveries.value];
    }
  }

  function selectDestination(params: any) {
    if (params === -1) {
      filter.selectedDestinations = me.hub as any;
      filter.selectedDestinationsId = me.hubId as any;
    } else {
      filter.selectedDestinations = params as any;
      filter.selectedDestinationsId = params.id as any;
    }
    selectedDeliveries.value = [];
    selectedDeliveriesId.value = [];
  }

  function moveDelivery(index: number, direction = 1) {
    // gather id of delivery that pretend to move
    const mainId = deliveries.value[index].id;

    // try to store delivery that will replace soon.
    const newPositionId = deliveries.value[index + direction].id;

    // Check is both delivery are in selected array.
    const currentIndex = selectedDeliveriesId.value.indexOf(mainId);
    const newPositionIndex = selectedDeliveriesId.value.indexOf(newPositionId);

    /* eslint-disable */
    // if both are in selected array.
    if (currentIndex > -1 && newPositionIndex > -1) {
      // swap position of both delivery in selected array.
      selectedDeliveries.value[newPositionIndex] = selectedDeliveries.value.splice(currentIndex, 1, selectedDeliveries.value[newPositionIndex])[0];
      selectedDeliveriesId.value[newPositionIndex] = selectedDeliveriesId.value.splice(currentIndex, 1, selectedDeliveriesId.value[newPositionIndex])[0];
    }
    // perform swap position.
    deliveries.value[index + direction] = deliveries.value.splice(index, 1, deliveries.value[index + direction])[0];
    /* eslint-enable */
  }

  const { result: getCountDeliveryGroups, refetch: refetchGetCountDeliveryGroups } = useQuery(countDeliveryGroups, (() => ({
    filterDeliveryNoteId: undefined,
    filterStatus: 'ACCEPT_SHIPMENT_BY_SHIPPING_PROVIDER',
    // filterDestinationHubId: me.hubId,
  })), () => ({ fetchPolicy: 'network-only' }));
  const countDelivery = useResult(getCountDeliveryGroups, {
    area: [],
    destination: [],
  }, (data: any) => data);

  const computedSelectableFilter = computed(() => ({
    area: areaCodes.value?.map((a: any) => {
      let count = 0;
      for (let i = 0; i < countDelivery.value.area.length; i += 1) {
        // eslint-disable-next-line
        if (a.id == countDelivery.value.area[i].id) {
          count = countDelivery.value.area[i].count;
        }
      }
      return {
        ...a,
        count,
      };
    }),
    destination: me.company.hubs.map((a: any) => {
      let count = 0;
      for (let i = 0; i < countDelivery.value?.destination.length; i += 1) {
        // eslint-disable-next-line
        if (a.id == countDelivery.value?.destination[i].id) count = countDelivery.value?.destination[i].count;
      }
      return {
        ...a,
        count,
      };
    }),
  }));

  const viewOnlySelected = ref(false);
  // eslint-disable-next-line
  const computedDeliveries = computed(() => viewOnlySelected.value ? selectedDeliveries.value : deliveries.value);
  function toggleViewOnlySelected() {
    viewOnlySelected.value = !viewOnlySelected.value;
  }

  return {
    filter,

    areaCodes,
    selectedAreaCode,
    countResend,
    mostStableTotalItem,

    deliveries,
    deliveriesPage,
    getDeliveriesLoading,
    deliveryNameSearchHandler,
    deliveriesPerPage,
    deliveriesResult,
    deliveriesPaginatorInfo,
    selectedDeliveriesId,
    selectedDeliveries,
    deliverySortHandler,
    pendingSelectAllDeliveries,
    selectDelivery,
    moveDelivery,

    computedSelectableFilter,
    selectDestination,

    viewOnlySelected,
    computedDeliveries,
    toggleViewOnlySelected,
  };
}
